import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Keyboard,
  ScrollView,
  StyleSheet,
  View,
  Platform,
  Animated,
  KeyboardAvoidingView,
  TouchableOpacity,
  Text,
  Dimensions,
  Switch,
  Easing,
} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {isTablet} from 'react-native-device-info';
import Orientation from 'react-native-orientation-locker';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {
  Users,
  User,
  Plane,
  Armchair,
  Zap,
  ArrowUp,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Pause,
  PlayCircle,
} from '../../../components/Icons';

import {I18nContext} from 'i18n/context/I18nContext';
import World from 'components/World';
import WorldSettingsListItems from 'screens/Meeting/components/SideBar/Settings/WorldSettingsListItems';
import SidePanel from 'screens/Meeting/components/SidePanel';
import {
  startOneOnOneMeeting,
  stopOneOnOneMeeting,
} from 'store/communication/actions/speechToTextServiceActions';
import VideoThumbnail from 'components/Communication/VideoThumbnail';
import {
  setCurrentLessonProgress,
  setStartLastQuestionOneToOne,
} from '../../../store/course';
import {setLessonPaused, setLessonStarted} from 'store/meeting/actions/meeting';
import {setIsViewboardContentFullScreen} from 'store/viewboard';
import {useRemScale} from 'lib/remScale';
import useLessonProgressUpdater from '../../../hooks/useCourseProgressUpdater';
import getRemoteConfigValue from 'utils/firebase/remoteConfig';
// Backend lesson tracking is deduped and handled by Unity `courseState` in `components/World/World.js`.

const Controls = ({onClose}) => {
  const {translate} = useContext(I18nContext);
  const dispatch = useDispatch();
  const {rem} = useRemScale();

  const [openEmotes, setOpenEmotes] = useState(false);
  const [isSitting, setIsSitting] = useState(false);
  const [isHandUp, setIsHandUp] = useState(false);
  const [isFlying, setIsFlying] = useState(false);
  const [isLaserOn, setIsLaserOn] = useState(false);
  const [isShowingSettings, setIsShowingSettings] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [isViewboardMode, setIsViewboardMode] = useState(false);
  const [isQuestionMode, setIsQuestionMode] = useState(false);
  const [isOneToOneMeetingMode, setOneToOneMeetingMode] = useState(false);
  // Shared cooldown for movement-related actions (jump, fly, sit).
  const [movementCooldown, setMovementCooldown] = useState(false);
  const movementCooldownTimeoutRef = useRef(null);

  // Auto-scroll state for lesson transcript
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [autoScrollUserControlled, setAutoScrollUserControlled] = useState(false);
  const [wordRatePerSecond, setWordRatePerSecond] = useState(3);

  // Lesson playback state (Unity-driven).
  // React must keep itself in sync when entering/leaving Tutor mode so lesson
  // playback doesn't get "stuck" when Unity is new but the web bridge/UI is old.
  const lessonStarted = useSelector(state => state.meeting.meeting.lessonStarted);
  const lessonPaused = useSelector(state => state.meeting.meeting.lessonPaused);
  const [enteredTutorFromPlayingLesson, setEnteredTutorFromPlayingLesson] =
    useState(false);
  const [enteredTutorFromLastQuestion, setEnteredTutorFromLastQuestion] =
    useState(false);

  const direct_landing = useSelector(
    state => state.meeting.meeting.direct_landing,
  );
  const ready_to_move = useSelector(
    state => state.meeting.meeting.ready_to_move,
  );
  const legacy = useSelector(state => state.meeting.meeting.legacy);

  const courseComplete = useSelector(
    state => state.communication.course.courseComplete,
  );
  const isQuikbase = useSelector(state => state.meeting.meeting.isQuikbase);

  const currentLessonProgress = useSelector(
    state => state.course.currentLessonProgress,
  );
  const currentLessonId = currentLessonProgress?.currentLessonId || '';

  // Viewboard fullscreen state (sidebar viewboards)
  const isViewboardContentFullScreen = useSelector(
    state => state.viewboard.isViewboardContentFullScreen,
  );
  const selectedViewboardId = useSelector(
    state => state.viewboard.selectedViewboardId,
  );
  const activeViewboards = useSelector(state => state.viewboard.activeViewboards);

  const startLastQuestionOneToOne = useSelector(
    state => state.course.startLastQuestionOneToOne,
  );

  // Lesson transcript panel selectors
  const lessonControls = useSelector(
    state => state.meeting.meeting.lessonControls,
  );
  const lessonTextByLessonId = useSelector(
    state => state.meeting.meeting.lessonTextByLessonId,
  );
  const aiTeacherName = useSelector(
    state => state.app.courseTokenValues?.AITeacherToLoad,
  );

  const cachedLessonTextForLesson =
    (currentLessonId && lessonTextByLessonId?.[currentLessonId]) || '';
  const effectiveLessonText = (() => {
    const live = lessonControls?.lessonText || '';
    if (String(live).trim().length) return live;
    return cachedLessonTextForLesson || '';
  })();

  // We don't want clunky/partial transcript rendering. Only show text once it has "settled"
  // (i.e., stopped changing for a short debounce window). Until then, render nothing.
  const [finalLessonText, setFinalLessonText] = useState('');
  
  // Selectors for backend progress persistence
  const token = useSelector(state => state.meeting.properties?.token);
  const unitId = useSelector(state => state.app.courseTokenValues?.unitId);
  const userId = useSelector(state => state.app.courseTokenValues?.userId);

  // Hook to sync lesson progress to courseData
  const { updateLessonProgressStates } = useLessonProgressUpdater();

  // Sync currentLessonProgress to courseData when slides are viewed
  // This runs in Controls because LessonMenuOverlay is unmounted during lesson play
  useEffect(() => {
    if (
      currentLessonProgress?.currentLessonId &&
      currentLessonProgress?.currentLessonProgressPercentage !== undefined &&
      currentLessonProgress?.currentLessonProgressPercentage > 0
    ) {
      console.log('[Controls] Syncing lesson progress to courseData:', {
        lessonId: currentLessonProgress.currentLessonId,
        progress: currentLessonProgress.currentLessonProgressPercentage,
      });
      updateLessonProgressStates({
        lessonId: currentLessonProgress.currentLessonId,
        newProgress: currentLessonProgress.currentLessonProgressPercentage,
        newLessonMasteryScore: currentLessonProgress.currentLessonMasteryScore || 0,
      });
    }
  }, [currentLessonProgress?.currentLessonProgressPercentage, currentLessonProgress?.currentLessonId]);

  const [isPortrait, setIsPortrait] = useState(
    Dimensions.get('window').width < Dimensions.get('window').height,
  );

  // Scale animations for right-side action tiles
  const buttonScale = useRef({
    fullscreen: new Animated.Value(1),
    tutor: new Animated.Value(1),
    fly: new Animated.Value(1),
    sit: new Animated.Value(1),
    laser: new Animated.Value(1),
    jump: new Animated.Value(1),
    help: new Animated.Value(1),
  }).current;

  useEffect(() => {
    const updateOrientation = ({window}) => {
      setIsPortrait(window.width < window.height);
    };

    const subscription = Dimensions.addEventListener('change', updateOrientation);
    return () => {
      subscription?.remove();
    };
  }, []);

  const startMovementCooldown = useCallback(() => {
    // 2s cooldown window for movement actions
    const COOLDOWN_MS = 2000;
    try {
      if (movementCooldownTimeoutRef.current) {
        clearTimeout(movementCooldownTimeoutRef.current);
      }
      setMovementCooldown(true);
      movementCooldownTimeoutRef.current = setTimeout(() => {
        setMovementCooldown(false);
        movementCooldownTimeoutRef.current = null;
      }, COOLDOWN_MS);
    } catch (_) {
      // Fail-safe: if anything goes wrong, make sure cooldown doesn't get stuck forever.
      setMovementCooldown(false);
      movementCooldownTimeoutRef.current = null;
    }
  }, []);

  const location = useSelector(state => state.world.location);
  const [currentLocation, setCurrentLocation] = useState(location);

  useEffect(() => {
    const clearEmoteState = () => {
      setOpenEmotes(false);
      setIsSitting(false);
      setIsHandUp(false);
      setIsFlying(false);
      setIsLaserOn(false);

      World.runCommand('perform_emote sit false');
      World.runCommand('perform_emote handraise false');
      World.runCommand('perform_emote fly false');
      World.runCommand('perform_emote laser false');
    };

    if (currentLocation !== location) {
      clearEmoteState();
      setCurrentLocation(location);
    }
  }, [currentLocation, location]);

  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardDidShow',
      () => setKeyboardVisible(true),
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      () => setKeyboardVisible(false),
    );

    return () => {
      keyboardDidHideListener.remove();
      keyboardDidShowListener.remove();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (movementCooldownTimeoutRef.current) {
        clearTimeout(movementCooldownTimeoutRef.current);
        movementCooldownTimeoutRef.current = null;
      }
    };
  }, []);

  const toggleViewboardMode = () => {
    setIsViewboardMode(!isViewboardMode);
    World.runCommand(`viewboard_fullscreen viewboard ${!isViewboardMode}`);
  };

  // If fullscreen is enabled but the fullscreen toggle is no longer visible (e.g. lesson ended),
  // we still need a safe way to exit fullscreen programmatically.
  const exitViewboardFullscreenIfNeeded = useCallback(
    (reason = 'unknown') => {
      if (!isViewboardMode) return;
      try {
        World.runCommand('viewboard_fullscreen viewboard false');
      } catch (e) {
        console.warn(
          `[Controls] viewboard_fullscreen false failed (reason=${reason})`,
          e,
        );
      }
      setIsViewboardMode(false);
    },
    [isViewboardMode],
  );

  // Force-exit ANY viewboard fullscreen mode we might be in before entering Tutor mode.
  // This covers both:
  // - Controls' local fullscreen toggle (viewboard "viewboard")
  // - Sidebar viewboard fullscreen state (selected/active viewboard id)
  const exitAllFullscreenIfNeeded = useCallback(
    (reason = 'enterTutor') => {
      exitViewboardFullscreenIfNeeded(reason);

      if (isViewboardContentFullScreen) {
        const vbId =
          selectedViewboardId || activeViewboards?.[0]?.id || 'viewboard';
        try {
          World.runCommand(`viewboard_fullscreen ${vbId} false`);
        } catch (e) {
          console.warn(
            `[Controls] viewboard_fullscreen ${vbId} false failed (reason=${reason})`,
            e,
          );
        }
        dispatch(setIsViewboardContentFullScreen(false));
      }
    },
    [
      exitViewboardFullscreenIfNeeded,
      isViewboardContentFullScreen,
      selectedViewboardId,
      activeViewboards,
      dispatch,
    ],
  );

  const toggleSettings = () => {
    setIsShowingSettings(!isSidePanelOpen);
    setIsSidePanelOpen(!isSidePanelOpen);
  };

  const pauseLessonIfNeededForTutor = () => {
    // Only pause if a lesson is started and currently playing. If it's already
    // paused, leave it as-is so we don't unexpectedly resume later.
    if (lessonStarted && !lessonPaused) {
      try {
        World.runCommand('pause_lesson true');
      } catch (e) {
        console.warn('[Controls] pause_lesson true for tutor failed', e);
      }
      try {
        // New Unity lesson contract routes pause/resume/next/prev via this command.
        World.runCommand('process_lesson_input pause');
      } catch (e) {
        console.warn('[Controls] process_lesson_input pause for tutor failed', e);
      }
      dispatch(setLessonPaused(true));
      setEnteredTutorFromPlayingLesson(true);
    } else {
      setEnteredTutorFromPlayingLesson(false);
    }
  };

  const resumeLessonIfTutorCameFromPlaying = () => {
    if (!enteredTutorFromPlayingLesson) return;
    // Only resume if the lesson is still marked started (it might have ended
    // while Tutor mode was active).
    if (!lessonStarted) {
      setEnteredTutorFromPlayingLesson(false);
      return;
    }
    try {
      World.runCommand('pause_lesson false');
    } catch (e) {
      console.warn('[Controls] pause_lesson false for tutor resume failed', e);
    }
    try {
      World.runCommand('process_lesson_input resume');
    } catch (e) {
      console.warn('[Controls] process_lesson_input resume after tutor failed', e);
    }
    dispatch(setLessonPaused(false));
    setEnteredTutorFromPlayingLesson(false);
  };

  const endLessonForLastQuestionTutor = () => {
    if (lessonStarted) {
      // Fully exit the lesson so when Tutor is toggled off we do NOT resume a
      // finished lesson.
      dispatch(setLessonStarted(false));
      dispatch(setLessonPaused(false));
      try {
        // Mirror mobile behavior: ensure Unity stops/pauses lesson systems.
        World.runCommand('pause_lesson true');
      } catch (e) {
        console.warn('[Controls] end lesson for last-question tutor failed', e);
      }
    }
    setEnteredTutorFromPlayingLesson(false);
    setEnteredTutorFromLastQuestion(true);
  };

  const toggleOneToOneMeetingMode = (options = {}) => {
    const {source = 'user'} = options; // 'user' | 'lastQuestion'
    const turningOn = !isOneToOneMeetingMode;

    // Keep lesson playback consistent with mobile:
    // - If user enters Tutor while a lesson is playing, pause it (and remember).
    // - If Tutor is triggered by the last question, treat lesson as finished/exited.
    // - When leaving Tutor, resume only if we entered from a playing lesson.
    if (turningOn) {
      // Requirement: whenever Tutor mode is enabled, force-exit fullscreen first.
      exitAllFullscreenIfNeeded(source === 'lastQuestion' ? 'autoTutor' : 'userTutor');
      if (source === 'lastQuestion') {
        endLessonForLastQuestionTutor();
      } else {
        pauseLessonIfNeededForTutor();
        setEnteredTutorFromLastQuestion(false);
      }
    } else {
      if (!enteredTutorFromLastQuestion) {
        resumeLessonIfTutorCameFromPlaying();
      } else {
        setEnteredTutorFromPlayingLesson(false);
      }
      setEnteredTutorFromLastQuestion(false);
    }

    toggleEuqations(isQuestionMode, turningOn, false);
    setOneToOneMeetingMode(turningOn);
    World.runCommand(`onetoone ${turningOn}`);
    console.log('toggleOneToOneMeetingMode', turningOn);
    if (turningOn) {
      dispatch(startOneOnOneMeeting());
    } else {
      dispatch(stopOneOnOneMeeting());
    }
  };

  const toggleEuqations = useCallback(
    (isQAMode, isOneTOOneMode, flag = false) => {
      console.log('toggleEuqations', isQAMode, isOneTOOneMode, flag);

      if (isQAMode && flag) {
        if (isOneTOOneMode) {
          setOneToOneMeetingMode(!isOneTOOneMode);
          World.runCommand(`onetoone ${!isOneTOOneMode}`);
          dispatch(stopOneOnOneMeeting());
        }
      } else if (isOneTOOneMode && !flag) {
        if (isQAMode) {
          setIsQuestionMode(!isQAMode);
          World.runCommand(`ask_question ${!isQAMode}`);
        }
      }
    },
    [],
  );

  const toggleQuestion = () => {
    toggleEuqations(!isQuestionMode, isOneToOneMeetingMode, true);
    setIsQuestionMode(!isQuestionMode);
    World.runCommand(`ask_question ${!isQuestionMode}`);
  };

  const toggleFlying = () => {
    // While in movement cooldown, ignore fly taps entirely.
    if (movementCooldown || !ready_to_move) return;
    // Do not allow entering Fly while sitting is active
    if (!isFlying && isSitting) return;

    setIsFlying(prev => {
      const next = !prev;
      try {
        World.runCommand(`perform_emote fly ${next}`);
      } catch (e) {
        console.warn('[Controls] perform_emote fly failed', e);
      }
      // Start cooldown when Fly turns ON or OFF, so fast taps can't
      // immediately re-toggle Fly or spam other movement controls.
      if ((!prev && next) || (prev && !next)) {
        startMovementCooldown();
      }
      return next;
    });
  };

  const toggleSitting = () => {
    // While in movement cooldown, ignore sit taps entirely.
    if (movementCooldown || !ready_to_move) return;
    // Do not allow entering Sit while flying is active
    if (!isSitting && isFlying) return;

    setIsSitting(prev => {
      const next = !prev;
      try {
        World.runCommand(`perform_emote sit ${next}`);
      } catch (e) {
        console.warn('[Controls] perform_emote sit failed', e);
      }
      // Start cooldown when Sit turns ON or OFF, so fast taps can't
      // immediately re-toggle Sit or spam other movement controls.
      if ((!prev && next) || (prev && !next)) {
        startMovementCooldown();
      }
      return next;
    });
  };

  const toggleHandUp = () => {
    setIsHandUp(!isHandUp);
    World.runCommand(`perform_emote handraise ${!isHandUp}`);
  };

  const onJump = () => {
    // While in movement cooldown, ignore jump taps entirely.
    if (movementCooldown || !ready_to_move) return;
    // Do not process Jump while Fly or Sit are active
    if (isFlying || isSitting) return;

    try {
      World.runCommand('perform_emote jump');
    } catch (e) {
      console.warn('[Controls] perform_emote jump failed', e);
    }
    startMovementCooldown();
  };

  const toggleLaser = () => {
    setIsLaserOn(!isLaserOn);
    World.runCommand(`perform_emote laser ${!isLaserOn}`);
  };

  const onFlyUp = () => {
    World.runCommand('perform_emote altitude true');
    flyingUpTimeout = setTimeout(onFlyUp, FLYING_TIMEOUT_INTERVAL);
  };

  const onStopFlyUp = () => {
    clearTimeout(flyingUpTimeout);
  };

  const onFlyDown = () => {
    World.runCommand('perform_emote altitude false');
    flyingDownTimeout = setTimeout(onFlyDown, FLYING_TIMEOUT_INTERVAL);
  };

  const onStopFlyDown = () => {
    clearTimeout(flyingDownTimeout);
  };

  const toggleSidePanel = isOpen => {
    setIsSidePanelOpen(isOpen);
  };

  const FLYING_TIMEOUT_INTERVAL = 35;
  let flyingUpTimeout = null;
  let flyingDownTimeout = null;

  const [currentOrientation, setCurrentOrientation] = useState(
    Orientation.getInitialOrientation(),
  );

  useEffect(() => {
    const onOrientationDidChange = orientation => {
      if (currentOrientation !== orientation) {
        setCurrentOrientation(orientation);
      }
    };
    Orientation.addOrientationListener(onOrientationDidChange);

    return () => {
      Orientation.removeOrientationListener(onOrientationDidChange);
    };
  }, [currentOrientation]);

  // Transcript panel refs and state
  const [lessonState, setLessonState] = useState('playing'); // 'playing' | 'paused'
  const transcriptScrollRef = useRef(null);
  const transcriptHeightsRef = useRef({contentHeight: 0, visibleHeight: 0});
  const totalWordsRef = useRef(0);
  const wordsSpokenRef = useRef(0);
  const autoScrollRafRef = useRef(null);
  const playbackLastTimestampRef = useRef(null);
  const latestAutoScrollYRef = useRef(0);
  const prevLessonStartedRef = useRef(false);
  const centerEnabledRef = useRef(false);
  const autoScrollEnabledRef = useRef(true);
  const trackingFrozenRef = useRef(false);
  const transcriptPanelAnimRef = useRef(new Animated.Value(0));
  const transcriptTextAnimRef = useRef(new Animated.Value(1));
  const lastAnimatedLessonTextRef = useRef('');

  const transcriptPanelAnim = transcriptPanelAnimRef.current;
  const transcriptTextAnim = transcriptTextAnimRef.current;

  const handleTranscriptLayout = useCallback(event => {
    transcriptHeightsRef.current.visibleHeight = event.nativeEvent.layout.height;
  }, []);

  const handleTranscriptContentSizeChange = useCallback(
    (contentWidth, contentHeight) => {
      transcriptHeightsRef.current.contentHeight = contentHeight;
    },
    [],
  );

  // Keep local lessonState in sync with Redux lessonPaused state
  // This ensures the UI reflects the current pause/play state
  useEffect(() => {
    if (lessonPaused) {
      setLessonState('paused');
    } else {
      setLessonState('playing');
    }
  }, [lessonPaused]);

  // Debounce lesson text before rendering to avoid "typing"/partial updates.
  useEffect(() => {
    if (!lessonStarted) {
      setFinalLessonText('');
      return;
    }
    if (!currentLessonId) {
      setFinalLessonText('');
      return;
    }

    const next = String(effectiveLessonText || '');
    if (!next.trim().length) {
      setFinalLessonText('');
      return;
    }

    const t = setTimeout(() => {
      setFinalLessonText(next);
    }, 250);

    return () => clearTimeout(t);
  }, [lessonStarted, currentLessonId, effectiveLessonText]);

  useEffect(() => {
    const isVisible = !isQuikbase && lessonStarted && !isOneToOneMeetingMode;

    if (!isVisible) {
      transcriptPanelAnim.setValue(0);
      return;
    }

    transcriptPanelAnim.setValue(0);
    Animated.timing(transcriptPanelAnim, {
      toValue: 1,
      duration: 320,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [lessonStarted, isOneToOneMeetingMode, isQuikbase, transcriptPanelAnim]);

  useEffect(() => {
    const next = String(finalLessonText || '').trim();

    if (!next) {
      lastAnimatedLessonTextRef.current = '';
      return;
    }

    if (lastAnimatedLessonTextRef.current === next) return;
    lastAnimatedLessonTextRef.current = next;

    transcriptTextAnim.setValue(0);
    Animated.parallel([
      Animated.timing(transcriptTextAnim, {
        toValue: 1,
        duration: 260,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [finalLessonText, transcriptTextAnim]);

  // Always scroll transcript to the top whenever the lesson text changes
  useEffect(() => {
    if (transcriptScrollRef?.current) {
      try {
        transcriptScrollRef.current.scrollTo({y: 0, animated: false});
      } catch (e) {
        console.warn('[Controls] Failed to scroll transcript to top', e);
      }
    }
  }, [finalLessonText]);

  // Keep track of total words in the current lesson text for proportional auto-scroll
  useEffect(() => {
    const rawText = finalLessonText || '';
    const words = rawText.trim().length ? rawText.trim().split(/\s+/) : [];
    totalWordsRef.current = words.filter(Boolean).length;
    // Reset spoken words & timing whenever the lesson text changes
    wordsSpokenRef.current = 0;
    playbackLastTimestampRef.current = null;
  }, [finalLessonText]);

  // Whenever a NEW lesson starts, auto-scroll should default to ON
  useEffect(() => {
    const prev = prevLessonStartedRef.current;
    if (!prev && lessonStarted) {
      setAutoScrollEnabled(true);
      setAutoScrollUserControlled(false);
      latestAutoScrollYRef.current = 0;
      wordsSpokenRef.current = 0;
      playbackLastTimestampRef.current = null;
      if (transcriptScrollRef?.current) {
        try {
          transcriptScrollRef.current.scrollTo({y: 0, animated: false});
        } catch (_) {}
      }
    }
    prevLessonStartedRef.current = lessonStarted;
  }, [lessonStarted]);

  // Load tutor speaking rate from Remote Config
  useEffect(() => {
    try {
      const rcValue = getRemoteConfigValue('video_playback_word_rate_per_second');
      let numeric = rcValue?.asNumber?.();
      if (typeof numeric !== 'number' || Number.isNaN(numeric) || numeric <= 0) {
        const str = rcValue?.asString?.();
        const parsed = parseFloat(str);
        if (!Number.isNaN(parsed) && parsed > 0) {
          numeric = parsed;
        }
      }
      if (typeof numeric === 'number' && !Number.isNaN(numeric) && numeric > 0) {
        setWordRatePerSecond(numeric);
      }
    } catch (e) {
      console.warn(
        '[Controls] Failed to read video_playback_word_rate_per_second from Remote Config',
        e,
      );
    }
  }, []);

  // Derived button interaction flags for lesson controls
  const buttonInteraction = lessonControls?.buttonInteraction || {};
  const previousEnabled = buttonInteraction.previous ?? false;
  const nextEnabled = buttonInteraction.next ?? false;
  const pauseInteract = buttonInteraction.pause ?? false;
  const resumeInteract = buttonInteraction.resume ?? false;
  // Gate the pause/next/previous controls SOLELY on Unity's interaction flags (matches
  // mobile). During a slide transition Unity sets pause=false until the tutor is actually
  // ready to speak; the previous slide's text is still present, so a text-based fallback
  // here would keep pause/next/prev clickable. Pressing pause in that window flips the UI
  // to "paused" but Unity is mid-transition and starts speaking anyway. Trusting Unity's
  // flags disables the controls during loading and avoids that conflicting input.
  const centerInteractionEnabled = pauseInteract || resumeInteract;

  // Update refs for timer callbacks
  useEffect(() => {
    centerEnabledRef.current = !!centerInteractionEnabled;
  }, [centerInteractionEnabled]);

  useEffect(() => {
    autoScrollEnabledRef.current = !!autoScrollEnabled;
  }, [autoScrollEnabled]);

  useEffect(() => {
    if (!centerInteractionEnabled) {
      trackingFrozenRef.current = true;
      playbackLastTimestampRef.current = null;
      return;
    }
    trackingFrozenRef.current = false;
    playbackLastTimestampRef.current = null;
  }, [centerInteractionEnabled]);

  // Auto-scroll transcript while lesson is playing
  useEffect(() => {
    const hasText = !!finalLessonText;

    if (lessonState !== 'playing' || !hasText) {
      playbackLastTimestampRef.current = null;
      if (autoScrollRafRef.current) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
      return;
    }

    if (autoScrollRafRef.current) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }

    playbackLastTimestampRef.current = null;

    const step = timestamp => {
      if (!transcriptScrollRef.current) {
        autoScrollRafRef.current = requestAnimationFrame(step);
        return;
      }

      if (!centerEnabledRef.current || trackingFrozenRef.current) {
        playbackLastTimestampRef.current = null;
        autoScrollRafRef.current = requestAnimationFrame(step);
        return;
      }

      if (!playbackLastTimestampRef.current) {
        playbackLastTimestampRef.current = timestamp;
        autoScrollRafRef.current = requestAnimationFrame(step);
        return;
      }

      const deltaSec = (timestamp - playbackLastTimestampRef.current) / 1000;
      playbackLastTimestampRef.current = timestamp;

      const totalWords = totalWordsRef.current || 0;
      if (totalWords > 0 && wordRatePerSecond > 0) {
        const deltaWords = deltaSec * wordRatePerSecond;
        const nextSpoken = Math.min(
          totalWords,
          (wordsSpokenRef.current || 0) + deltaWords,
        );
        wordsSpokenRef.current = nextSpoken;

        const fraction = totalWords > 0 ? nextSpoken / totalWords : 0;
        const {contentHeight, visibleHeight} = transcriptHeightsRef.current;
        const maxScrollY = Math.max(
          0,
          (contentHeight || 0) - (visibleHeight || 0),
        );
        if (maxScrollY > 0) {
          const targetY = fraction * maxScrollY;
          latestAutoScrollYRef.current = targetY;

          if (centerEnabledRef.current && autoScrollEnabledRef.current) {
            try {
              transcriptScrollRef.current.scrollTo({
                y: targetY,
                animated: false,
              });
            } catch (e) {
              console.warn('[Controls] Auto-scroll failed', e);
            }
          }
        }
      }

      autoScrollRafRef.current = requestAnimationFrame(step);
    };

    autoScrollRafRef.current = requestAnimationFrame(step);

    return () => {
      if (autoScrollRafRef.current) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
    };
  }, [lessonState, finalLessonText, wordRatePerSecond]);

  // Slide tracking for prev/next buttons
  const totalSlides = Math.max(
    0,
    Number(currentLessonProgress?.currentLessonTotalSlides || 0),
  );
  const startPct = Number(
    currentLessonProgress?.currentLessonProgressPercentage || 0,
  );
  const initialSlideIdx =
    totalSlides > 0
      ? Math.max(
          0,
          Math.min(totalSlides - 1, Math.round((startPct / 100) * totalSlides) - 1),
        )
      : 0;
  const [slideIndex, setSlideIndex] = useState(initialSlideIdx);

  useEffect(() => {
    if (!lessonStarted) return;
    setSlideIndex(initialSlideIdx);
  }, [lessonStarted, initialSlideIdx]);

  // Gate progress updates until slideIndex is synced to the new lesson.
  // This avoids a race where slideIndex from the previous lesson (often "last slide")
  // is briefly used with the new lesson's totalSlides, incorrectly promoting it to 100%.
  const slideIndexSyncedForLessonRef = useRef('');
  const awaitingSlideSyncRef = useRef(false);

  // Critical: when switching lessons, `slideIndex` from the previous lesson can briefly persist
  // while `currentLessonProgress` updates. If we compute progress during that window, the new
  // lesson can be incorrectly promoted to 100%. Use a layout effect to reset before paint/effects.
  const lastLessonIdRef = useRef('');
  useLayoutEffect(() => {
    if (!lessonStarted) {
      lastLessonIdRef.current = '';
      slideIndexSyncedForLessonRef.current = '';
      awaitingSlideSyncRef.current = false;
      return;
    }
    if (!currentLessonId) return;
    if (lastLessonIdRef.current !== currentLessonId) {
      lastLessonIdRef.current = currentLessonId;
      awaitingSlideSyncRef.current = true;
      setSlideIndex(initialSlideIdx);
    }
  }, [lessonStarted, currentLessonId, initialSlideIdx]);

  useEffect(() => {
    if (!lessonStarted) return;
    if (!currentLessonId) return;
    if (!awaitingSlideSyncRef.current) {
      // Already synced or not needed.
      slideIndexSyncedForLessonRef.current = currentLessonId;
      return;
    }
    if (slideIndex === initialSlideIdx) {
      slideIndexSyncedForLessonRef.current = currentLessonId;
      awaitingSlideSyncRef.current = false;
    }
  }, [lessonStarted, currentLessonId, slideIndex, initialSlideIdx]);

  // IMPORTANT:
  // We intentionally do NOT infer slideIndex from lesson transcript text changes.
  // Some slides can share identical text, and React-side dedupe can suppress updates,
  // which previously caused nav state (e.g. Next button) to get stuck at the last slide.

  // Update progress when slideIndex changes (UI/Redux). Backend tracking is handled by Unity `courseState`.
  useEffect(() => {
    if (!lessonStarted || totalSlides <= 0) return;
    if (!currentLessonId) return;

    // Hard guard: don't compute/progress-promote until slideIndex has synced for this lesson.
    if (slideIndexSyncedForLessonRef.current !== currentLessonId) return;

    // Guard: if slideIndex is out of range (stale from a previous lesson), don't update progress.
    const maxIdx = Math.max(0, totalSlides - 1);
    if (slideIndex < 0 || slideIndex > maxIdx) return;
    
    // Calculate progress based on slides viewed: (slideIndex + 1) / totalSlides * 100
    const slidesViewed = slideIndex + 1;
    const calculatedProgress = Math.round((slidesViewed / totalSlides) * 100);
    const currentProgress = currentLessonProgress?.currentLessonProgressPercentage || 0;
    // Reserve 100% for lessonEnded — it is set only AFTER the last slide finishes speaking
    // AND the 100% track-update succeeds (see World.js). Reaching the last slide must NOT
    // promote the UI to 100% on its own, otherwise exiting on the last slide before it ends
    // shows a false 100% that isn't persisted. Defer it (matches World.js courseState, which
    // also caps below 100 and finalizes 100% only on lessonEnded).
    if (calculatedProgress >= 100) return;
    const newProgress = Math.min(100, Math.max(calculatedProgress, currentProgress));
    
    // Only update if progress has changed
    if (newProgress > currentProgress && currentLessonProgress?.currentLessonId) {
      console.log('[Controls] Updating progress from slideIndex:', {
        slideIndex,
        slidesViewed,
        totalSlides,
        calculatedProgress,
        currentProgress,
        newProgress,
        lessonId: currentLessonProgress.currentLessonId,
      });
      
      // Update Redux state
      dispatch(setCurrentLessonProgress({
        currentLessonId: currentLessonProgress.currentLessonId,
        currentLessonProgressPercentage: newProgress,
        currentLessonTotalSlides: totalSlides,
        currentLessonSlidesWatched: slidesViewed,
      }));
      
      // NOTE: Do not POST tracking here. This avoids duplicate calls/payloads.
      // `components/World/World.js` posts to the backend using Unity `courseState`.
    }
  }, [slideIndex, lessonStarted, totalSlides, currentLessonProgress?.currentLessonId, legacy, token, unitId, userId]);

  const atFirstSlide = totalSlides > 0 ? slideIndex <= 0 : !previousEnabled;
  const atLastSlide = totalSlides > 0 ? slideIndex >= totalSlides - 1 : !nextEnabled;

  const previousDisabled = !centerInteractionEnabled || atFirstSlide;
  const nextDisabled = !centerInteractionEnabled || atLastSlide;
  const centerDisabled = !centerInteractionEnabled;

  // Auto-scroll toggle state
  const autoScrollToggleDisabled = !centerInteractionEnabled;
  const autoScrollTrackColor = autoScrollToggleDisabled
    ? {false: '#4B5563', true: '#4B5563'}
    : {false: '#4B5563', true: '#C5E836'};
  const autoScrollThumbColor = autoScrollToggleDisabled
    ? '#6B7280'
    : autoScrollEnabled
    ? '#111827'
    : '#9CA3AF';
  const toggleScale = Math.min(1, Math.max(0.55, rem(18) / 28));

  const sendLessonInput = input => {
    try {
      if ((input === 'next' || input === 'previous') && lessonStarted && totalSlides > 0) {
        const delta = input === 'previous' ? -1 : 1;
        setSlideIndex(prev => {
          const maxIdx = Math.max(0, totalSlides - 1);
          const next = Math.max(0, Math.min(maxIdx, (prev ?? 0) + delta));
          return next;
        });
      }
      World.runCommand(`process_lesson_input ${input}`);
    } catch (e) {
      console.warn('[Controls] process_lesson_input failed', input, e);
    }
  };

  const handleLessonControl = () => {
    if (lessonState === 'playing') {
      setLessonState('paused');
      dispatch(setLessonPaused(true));
      sendLessonInput('pause');
    } else {
      setLessonState('playing');
      dispatch(setLessonPaused(false));
      sendLessonInput('resume');
    }
  };

  const markAutoScrollUserControlled = useCallback(() => {
    if (!autoScrollUserControlled) {
      setAutoScrollUserControlled(true);
    }
  }, [autoScrollUserControlled]);

  const handleAutoScrollToggleChange = useCallback(
    next => {
      if (autoScrollToggleDisabled) return;
      markAutoScrollUserControlled();
      setAutoScrollEnabled(next);
      if (next && transcriptScrollRef.current) {
        try {
          transcriptScrollRef.current.scrollTo({
            y: latestAutoScrollYRef.current || 0,
            animated: true,
          });
        } catch (e) {
          console.warn('[Controls] Failed to jump to auto-scroll position', e);
        }
      }
    },
    [autoScrollToggleDisabled, markAutoScrollUserControlled],
  );

  const handleTranscriptManualScrollBegin = useCallback(() => {
    if (!centerInteractionEnabled) return;
    markAutoScrollUserControlled();
    setAutoScrollEnabled(false);
  }, [markAutoScrollUserControlled, centerInteractionEnabled]);

  const renderLessonIcon = () => {
    if (lessonState === 'playing') {
      return <Pause size={rem(24)} color="#000" strokeWidth={2.5} fill="#000" />;
    }
    return <PlayCircle size={rem(24)} color="#000" strokeWidth={2.5} />;
  };

  // Movement button disabled states:
  // - We do NOT visually disable buttons during the shared 2s cooldown;
  //   during cooldown, handlers simply early-return.
  // - Buttons are only disabled when the world is not ready_to_move.
  const movementGroupDisabled = !ready_to_move;
  const jumpDisabledComputed = movementGroupDisabled;
  const flyDisabledComputed = movementGroupDisabled;
  const sitDisabledComputed = movementGroupDisabled;
  const highlightBorderColor = 'rgba(197,232,54,0.30)';
  const transcriptPanelAnimatedStyle = {
    opacity: transcriptPanelAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0, 1],
    }),
    transform: [
      {
        translateY: transcriptPanelAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [rem(18), 0],
        }),
      },
      {
        scale: transcriptPanelAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [0.985, 1],
        }),
      },
    ],
  };
  const transcriptTextAnimatedStyle = {
    opacity: transcriptTextAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0.45, 1],
    }),
    transform: [
      {
        translateY: transcriptTextAnim.interpolate({
          inputRange: [0, 1],
          outputRange: [rem(6), 0],
        }),
      },
    ],
  };

  const styles = StyleSheet.create({
    full: {
      flex: 1,
    },
    container: {
      flex: 1,
    },
    panel: {
      position: 'absolute',
      width: Platform.OS === 'web' || isTablet() ? 375 : 300,
      height: '100%',
      right: 0,
      top: 0,
      flex: 1,
      opacity: 0.9,
      shadowColor: '#000',
      shadowOffset: {width: 4, height: 4},
      shadowOpacity: 0.3,
      shadowRadius: 5,
      elevation: 6,
    },
    buttonContainer: {
      position: 'absolute',
      right: isSidePanelOpen ? 250 : rem(24),
      // Raise buttons when caption panel or tutor input is visible
      bottom: !isQuikbase && lessonStarted && !isOneToOneMeetingMode
        ? rem(150) // Above updated caption panel
        : isOneToOneMeetingMode
        ? rem(150) // Match lessonStarted placement in tutor mode
        : rem(32),
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
    },
    actionTile: {
      width: rem(56),
      height: rem(56),
      borderRadius: rem(12),
      borderWidth: 1,
      borderColor: highlightBorderColor,
      backgroundColor: 'rgba(15,16,20,0.80)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionTileActive: {
      backgroundColor: 'rgba(197,232,54,0.20)',
      borderColor: highlightBorderColor,
      shadowColor: '#C5E836',
      shadowOpacity: 0.5,
      shadowRadius: rem(8),
      shadowOffset: {width: 0, height: rem(4)},
    },
    actionIcon: {
      marginBottom: rem(2),
    },
    actionLabel: {
      fontSize: rem(8),
      fontWeight: '700',
      letterSpacing: 0.5,
      color: '#C5E836',
    },
    // Transcript panel styles
    transcriptContainer: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: rem(20),
      zIndex: 20,
      alignItems: 'center',
      paddingHorizontal: rem(12),
    },
    transcriptInner: {
      width: '100%',
      maxWidth: isPortrait ? rem(760) : rem(1080),
      backgroundColor: 'rgba(27,36,53,0.66)',
      borderRadius: rem(18),
      borderWidth: 1,
      borderColor: 'rgba(197,232,54,0.24)',
      shadowColor: '#000',
      shadowOpacity: 0.4,
      shadowRadius: rem(22),
      shadowOffset: {width: 0, height: rem(10)},
      overflow: 'hidden',
    },
    transcriptTopGlow: {
      position: 'absolute',
      left: rem(14),
      right: rem(14),
      top: 0,
      height: 1,
      backgroundColor: 'rgba(236,252,203,0.38)',
      zIndex: 2,
    },
    transcriptRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: rem(14),
      paddingVertical: rem(14),
    },
    transcriptAvatar: {
      width: rem(36),
      height: rem(36),
      borderRadius: rem(18),
      marginRight: rem(12),
      borderWidth: 1,
      borderColor: 'rgba(197,232,54,0.35)',
      backgroundColor: 'rgba(9,12,20,0.45)',
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
    },
    transcriptTextContainer: {
      flex: 1,
      minWidth: 0,
    },
    transcriptScroll: {
      minHeight: rem(24),
      maxHeight: rem(120),
    },
    transcriptScrollContent: {
      paddingRight: rem(4),
      paddingBottom: rem(2),
    },
    transcriptTextAnimatedWrap: {
      width: '100%',
    },
    transcriptText: {
      fontSize: rem(16),
      lineHeight: rem(24),
      color: 'rgba(246,251,255,0.96)',
      fontWeight: '500',
      letterSpacing: 0.2,
    },
    transcriptSpeaker: {
      color: '#E1F88A',
      fontWeight: '700',
      fontSize: rem(16),
    },
    transcriptControls: {
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: rem(8),
    },
    transcriptIconButton: {
      padding: rem(8),
      borderRadius: rem(11),
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(255,255,255,0.05)',
      borderWidth: 1,
      borderColor: 'rgba(197,232,54,0.2)',
    },
    transcriptIconButtonDisabled: {
      opacity: 0.35,
    },
    transcriptPlayButton: {
      padding: rem(13),
      borderRadius: rem(13),
      backgroundColor: 'rgba(197,232,54,0.92)',
      borderWidth: 1,
      borderColor: 'rgba(15,16,20,0.35)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    transcriptPlayButtonDisabled: {
      opacity: 0.45,
    },
    autoScrollToggle: {
      alignItems: 'center',
      marginLeft: rem(18),
    },
    autoScrollTogglePortrait: {
      width: rem(72),
    },
    autoScrollToggleLandscape: {
      marginTop: rem(8),
    },
  });

  React.useEffect(() => {
    if (ready_to_move && direct_landing && legacy) {
      toggleOneToOneMeetingMode();
    }
  }, [ready_to_move, direct_landing, legacy]);

  React.useEffect(() => {
    if (startLastQuestionOneToOne) {
      toggleOneToOneMeetingMode({source: 'lastQuestion'});
      dispatch(setStartLastQuestionOneToOne(false));
    }
  }, [startLastQuestionOneToOne]);

  const RightActionButtons = () => {
    const verticalGap = rem(8);
    const FullscreenIcon = ({size, color, style}) => (
      <Icon
        name={isViewboardMode ? 'fullscreen-exit' : 'fullscreen'}
        size={size}
        color={color}
        style={style}
      />
    );

    // Define the logical button list
    const items = [
      {
        key: 'fullscreen',
        label: 'FULL',
        IconComponent: FullscreenIcon,
        disabled: false,
        active: isViewboardMode,
        hidden: !lessonStarted,
        onPress: toggleViewboardMode,
      },
      {
        key: 'tutor',
        label: 'TUTOR',
        IconComponent: Users,
        disabled: !ready_to_move || isQuikbase,
        active: isOneToOneMeetingMode,
        hidden: isQuikbase,
        onPress: toggleOneToOneMeetingMode,
      },
      {
        key: 'fly',
        label: 'FLY',
        IconComponent: Plane,
        disabled: flyDisabledComputed,
        active: isFlying,
        hidden: isOneToOneMeetingMode || lessonStarted,
        onPress: toggleFlying,
      },
      {
        key: 'sit',
        label: 'SIT',
        IconComponent: Armchair,
        disabled: sitDisabledComputed,
        active: isSitting,
        hidden: isOneToOneMeetingMode || lessonStarted,
        onPress: toggleSitting,
      },
      {
        key: 'laser',
        label: 'LASER',
        IconComponent: Zap,
        disabled: !ready_to_move,
        active: isLaserOn,
        hidden: isOneToOneMeetingMode || lessonStarted,
        onPress: toggleLaser,
      },
      {
        key: 'jump',
        label: 'JUMP',
        IconComponent: ArrowUp,
        disabled: jumpDisabledComputed,
        active: false,
        hidden: isOneToOneMeetingMode || lessonStarted,
        onPress: onJump,
      },
      {
        key: 'help',
        label: 'HELP',
        IconComponent: HelpCircle,
        disabled: !ready_to_move,
        active: false,
        hidden: isOneToOneMeetingMode || lessonStarted,
        onPress: () => {
          if (Platform.OS !== 'web' || typeof window === 'undefined') return;
          const event =
            typeof window.CustomEvent === 'function'
              ? new window.CustomEvent('quiklearn:show-tutorial-video')
              : new Event('quiklearn:show-tutorial-video');
          window.dispatchEvent(event);
        },
      },
    ].filter(item => !item.hidden);

    const handleTilePress = useCallback((key, onPress, disabled) => {
      if (disabled) return;
      
      // Scale animation
      Animated.sequence([
        Animated.spring(buttonScale[key], {
          toValue: 0.95,
          useNativeDriver: true,
          friction: 6,
        }),
        Animated.spring(buttonScale[key], {
          toValue: 1,
          useNativeDriver: true,
          friction: 6,
        }),
      ]).start();

      if (typeof onPress === 'function') {
        onPress();
      }
    }, [buttonScale]);

    const renderItem = (item, index, totalLength) => {
      const {key, label, IconComponent, disabled, active, onPress} = item;
      const isLast = index === totalLength - 1;

      return (
        <Animated.View
          key={key}
          style={{
            transform: [{scale: buttonScale[key]}],
            marginBottom: isLast ? 0 : verticalGap,
          }}
        >
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => handleTilePress(key, onPress, disabled)}
            disabled={disabled}
            style={[
              styles.actionTile,
              active && styles.actionTileActive,
              disabled && {opacity: 0.5},
            ]}
          >
            <IconComponent
              size={rem(20)}
              color="#C5E836"
              strokeWidth={2.5}
              style={styles.actionIcon}
            />
            <Text style={styles.actionLabel}>{label}</Text>
          </TouchableOpacity>
        </Animated.View>
      );
    };

    return (
      <View style={{alignItems: 'flex-end'}}>
        {items.map((item, idx) => renderItem(item, idx, items.length))}
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      pointerEvents="box-none"
      style={{flex: 1}}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <View style={styles.full} pointerEvents="box-none">
        {isSidePanelOpen ? (
          <SidePanel onPanelStateChange={toggleSidePanel}>
            <ScrollView style={styles.container}>
              <WorldSettingsListItems />
            </ScrollView>
          </SidePanel>
        ) : null}
        
        {/* Right-side action buttons */}
        <View style={styles.buttonContainer} pointerEvents="box-none">
          <RightActionButtons />
        </View>

        {/* Bottom lesson transcript panel - appears when lesson is active */}
        {!isQuikbase && lessonStarted && !isOneToOneMeetingMode && (
          <View
            style={styles.transcriptContainer}
            pointerEvents="box-none">
            <Animated.View
              style={[styles.transcriptInner, transcriptPanelAnimatedStyle]}>
              <View style={styles.transcriptTopGlow} />
              {isPortrait ? (
                <>
                  <View style={styles.transcriptRow}>
                    {/* Speaker avatar */}
                    <View style={styles.transcriptAvatar}>
                      <User size={rem(24)} color="#C5E836" strokeWidth={2.5} />
                    </View>

                    {/* Text content */}
                    <View style={styles.transcriptTextContainer}>
                      <ScrollView
                        ref={transcriptScrollRef}
                        style={styles.transcriptScroll}
                        contentContainerStyle={styles.transcriptScrollContent}
                        showsVerticalScrollIndicator={false}
                        scrollEnabled={centerInteractionEnabled}
                        onLayout={handleTranscriptLayout}
                        onContentSizeChange={handleTranscriptContentSizeChange}
                        onScrollBeginDrag={handleTranscriptManualScrollBegin}>
                        <Animated.View
                          style={[
                            styles.transcriptTextAnimatedWrap,
                            transcriptTextAnimatedStyle,
                          ]}>
                          <Text style={styles.transcriptText}>
                            <Text style={styles.transcriptSpeaker}>
                              {(aiTeacherName || 'Tutor') + ': '}
                            </Text>
                            {finalLessonText || ''}
                          </Text>
                        </Animated.View>
                      </ScrollView>
                    </View>
                  </View>

                  {/* Portrait: controls row centered below the transcript text */}
                  <View
                    style={{
                      paddingVertical: rem(10),
                      alignItems: 'center',
                      marginTop: rem(-8),
                    }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        paddingHorizontal: rem(24),
                      }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 1,
                        }}>
                        <View style={styles.transcriptControls}>
                          <TouchableOpacity
                            activeOpacity={0.85}
                            style={[
                              styles.transcriptIconButton,
                              previousDisabled &&
                                styles.transcriptIconButtonDisabled,
                            ]}
                            disabled={previousDisabled}
                            onPress={() => sendLessonInput('previous')}>
                            <ChevronLeft
                              size={rem(24)}
                              color="#C5E836"
                              strokeWidth={2.5}
                            />
                          </TouchableOpacity>

                          <View style={{width: rem(8)}} />

                          <TouchableOpacity
                            activeOpacity={0.9}
                            style={[
                              styles.transcriptPlayButton,
                              centerDisabled &&
                                styles.transcriptPlayButtonDisabled,
                            ]}
                            onPress={handleLessonControl}
                            disabled={centerDisabled}>
                            {renderLessonIcon()}
                          </TouchableOpacity>

                          <View style={{width: rem(8)}} />

                          <TouchableOpacity
                            activeOpacity={0.85}
                            style={[
                              styles.transcriptIconButton,
                              nextDisabled && styles.transcriptIconButtonDisabled,
                            ]}
                            disabled={nextDisabled}
                            onPress={() => sendLessonInput('next')}>
                            <ChevronRight
                              size={rem(24)}
                              color="#C5E836"
                              strokeWidth={2.5}
                            />
                          </TouchableOpacity>

                          {/* Fullscreen toggle */}
                          <View style={{width: rem(8)}} />

                          <TouchableOpacity
                            activeOpacity={0.85}
                            style={[
                              styles.transcriptIconButton,
                              centerDisabled &&
                                styles.transcriptIconButtonDisabled,
                            ]}
                            disabled={centerDisabled}
                            onPress={toggleViewboardMode}>
                            <Icon
                              name={
                                isViewboardMode ? 'fullscreen-exit' : 'fullscreen'
                              }
                              size={rem(22)}
                              color="#C5E836"
                            />
                          </TouchableOpacity>
                        </View>

                        {/* Auto-scroll toggle */}
                        <View
                          style={[
                            styles.autoScrollToggle,
                            styles.autoScrollTogglePortrait,
                          ]}>
                          <Switch
                            value={autoScrollEnabled}
                            onValueChange={handleAutoScrollToggleChange}
                            disabled={autoScrollToggleDisabled}
                            thumbColor={autoScrollThumbColor}
                            trackColor={autoScrollTrackColor}
                            style={{transform: [{scale: toggleScale}]}}
                          />
                          <Text
                            style={{
                              color: '#C5E836',
                              fontSize: rem(10),
                              fontWeight: '600',
                              marginTop: rem(4),
                              textAlign: 'center',
                            }}>
                            AUTO SCROLL
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>
                </>
              ) : (
                // Landscape layout
                <View style={styles.transcriptRow}>
                  {/* Speaker avatar */}
                  <View style={styles.transcriptAvatar}>
                    <User size={rem(24)} color="#C5E836" strokeWidth={2.5} />
                  </View>

                  {/* Text content */}
                  <View style={styles.transcriptTextContainer}>
                    <ScrollView
                      ref={transcriptScrollRef}
                      style={styles.transcriptScroll}
                      contentContainerStyle={styles.transcriptScrollContent}
                      showsVerticalScrollIndicator={false}
                      scrollEnabled={centerInteractionEnabled}
                      onLayout={handleTranscriptLayout}
                      onContentSizeChange={handleTranscriptContentSizeChange}
                      onScrollBeginDrag={handleTranscriptManualScrollBegin}>
                      <Animated.View
                        style={[
                          styles.transcriptTextAnimatedWrap,
                          transcriptTextAnimatedStyle,
                        ]}>
                        <Text style={styles.transcriptText}>
                          <Text style={styles.transcriptSpeaker}>
                            {(aiTeacherName || 'Tutor') + ': '}
                          </Text>
                          {finalLessonText || ''}
                        </Text>
                      </Animated.View>
                    </ScrollView>
                  </View>

                  {/* Controls – previous / play-pause / next */}
                  <View style={styles.transcriptControls}>
                    <TouchableOpacity
                      activeOpacity={0.85}
                      style={[
                        styles.transcriptIconButton,
                        previousDisabled && styles.transcriptIconButtonDisabled,
                      ]}
                      disabled={previousDisabled}
                      onPress={() => sendLessonInput('previous')}>
                      <ChevronLeft
                        size={rem(24)}
                        color="#C5E836"
                        strokeWidth={2.5}
                      />
                    </TouchableOpacity>

                    <View style={{width: rem(8)}} />

                    <TouchableOpacity
                      activeOpacity={0.9}
                      style={[
                        styles.transcriptPlayButton,
                        centerDisabled && styles.transcriptPlayButtonDisabled,
                      ]}
                      onPress={handleLessonControl}
                      disabled={centerDisabled}>
                      {renderLessonIcon()}
                    </TouchableOpacity>

                    <View style={{width: rem(8)}} />

                    <TouchableOpacity
                      activeOpacity={0.85}
                      style={[
                        styles.transcriptIconButton,
                        nextDisabled && styles.transcriptIconButtonDisabled,
                      ]}
                      disabled={nextDisabled}
                      onPress={() => sendLessonInput('next')}>
                      <ChevronRight
                        size={rem(24)}
                        color="#C5E836"
                        strokeWidth={2.5}
                      />
                    </TouchableOpacity>

                    {/* Fullscreen toggle */}
                    <View style={{width: rem(8)}} />

                    <TouchableOpacity
                      activeOpacity={0.85}
                      style={[
                        styles.transcriptIconButton,
                        centerDisabled && styles.transcriptIconButtonDisabled,
                      ]}
                      disabled={centerDisabled}
                      onPress={toggleViewboardMode}>
                      <Icon
                        name={
                          isViewboardMode ? 'fullscreen-exit' : 'fullscreen'
                        }
                        size={rem(22)}
                        color="#C5E836"
                      />
                    </TouchableOpacity>
                  </View>

                  {/* Auto-scroll toggle */}
                  <View
                    style={[
                      styles.autoScrollToggle,
                      styles.autoScrollToggleLandscape,
                    ]}>
                    <Switch
                      value={autoScrollEnabled}
                      onValueChange={handleAutoScrollToggleChange}
                      disabled={autoScrollToggleDisabled}
                      thumbColor={autoScrollThumbColor}
                      trackColor={autoScrollTrackColor}
                      style={{transform: [{scale: toggleScale}]}}
                    />
                    <Text
                      style={{
                        color: '#C5E836',
                        fontSize: rem(10),
                        fontWeight: '600',
                        marginTop: rem(4),
                        textAlign: 'center',
                      }}>
                      AUTO SCROLL
                    </Text>
                  </View>
                </View>
              )}
            </Animated.View>
          </View>
        )}

        {/* Fly up/down controls - positioned above right buttons when flying */}
        {isFlying && (
          <View
            style={{
              position: 'absolute',
              right: rem(100),
              // Match the button container bottom positioning
              bottom: !isQuikbase && lessonStarted && !isOneToOneMeetingMode
                ? rem(150)
                : isOneToOneMeetingMode
                ? rem(150)
                : rem(32),
              alignItems: 'center',
              zIndex: 41,
            }}
            pointerEvents="auto">
            {/* Fly up */}
            <TouchableOpacity
              style={[styles.actionTile, {marginBottom: rem(8)}]}
              onPressIn={onFlyUp}
              onPressOut={onStopFlyUp}
              activeOpacity={0.9}>
              <ChevronUp size={rem(24)} color="#C5E836" strokeWidth={2.5} />
              <Text style={styles.actionLabel}>UP</Text>
            </TouchableOpacity>

            {/* Fly down */}
            <TouchableOpacity
              style={styles.actionTile}
              onPressIn={onFlyDown}
              onPressOut={onStopFlyDown}
              activeOpacity={0.9}>
              <ChevronDown size={rem(24)} color="#C5E836" strokeWidth={2.5} />
              <Text style={styles.actionLabel}>DOWN</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </KeyboardAvoidingView>
  );
};

Controls.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default Controls;
